import { ChangeDetectionStrategy, Component, Signal, computed, inject } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { HdLocksKeysReadingPanelId, getHdLocksKeysReadingPanel } from './hd-locks-keys-reading-panels';
import { HdLocksKeysSectionComponent } from './hd-locks-keys-section.component';

export interface HdLocksKeysReadingPanelData {
  readonly panel: HdLocksKeysReadingPanelId;
}

/**
 * Vervolgpaneel op de Locks & Keys-uitleg: één component voor de vier
 * leespanelen ("Mens na 2027", Biologische shift, Rave en Penta, Nodale
 * omgeving). Welk paneel, welke secties en welk icoon komt uit het register
 * in `hd-locks-keys-reading-panels.ts`; de content uit de
 * `human-design/locks-keys`-scope. De secties zelf rendert de gedeelde
 * `hd-locks-keys-section` (zelfde sectiemodel als het intro-paneel); dit
 * paneel voegt enkel de titel, de review-melding en het lead-kader toe.
 */
@Component({
  selector: 'hd-locks-keys-reading-panel',
  imports: [SharedModule, HdLocksKeysSectionComponent],
  templateUrl: './hd-locks-keys-reading-panel.component.html',
  styleUrl: './hd-locks-keys-reading-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLocksKeysReadingPanelComponent {
  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdLocksKeysReadingPanelData>
    | null;
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly definition = computed(() =>
    getHdLocksKeysReadingPanel(this.panelData?.()?.panel ?? 'postShift'),
  );

  protected readonly title = computed(() =>
    this.gateInfo.locksKeysTextIn(`${this.definition().textPrefix}.title`, this.lang()),
  );

  /** Uitgelicht kader bovenaan; leeg (en dus verborgen) voor panelen zonder lead. */
  protected readonly lead = computed(() =>
    this.definition().highlighted
      ? this.gateInfo.locksKeysTextIn(`${this.definition().textPrefix}.lead`, this.lang())
      : '',
  );

  /** Subtiele melding "nog in review" bovenaan; enkel voor panelen met een `reviewNotice`-tekst. */
  protected readonly reviewNotice = computed(() =>
    this.gateInfo.locksKeysTextIn(`${this.definition().textPrefix}.reviewNotice`, this.lang()),
  );

}

/**
 * Opent (of focust) een Locks & Keys-vervolgpaneel, gedockt in de volle zone
 * zoals de andere leespanelen. Gedeeld door de doorklikkaarten in het
 * intro-paneel en door ingangen buiten de modus (bv. de post-2027-badge).
 */
export function openHdLocksKeysReadingPanel(
  floatingPanels: FloatingPanelService,
  gateInfo: HdGateInfoService,
  lang: string,
  panel: HdLocksKeysReadingPanelId,
): void {
  const definition = getHdLocksKeysReadingPanel(panel);
  floatingPanels.openOrFocus<HdLocksKeysReadingPanelComponent, HdLocksKeysReadingPanelData>({
    key: definition.panelKey,
    component: HdLocksKeysReadingPanelComponent,
    data: { panel },
    dockTab: { label: gateInfo.locksKeysTextIn(`${definition.textPrefix}.tabLabel`, lang) },
    dockZone: 'full',
    closable: true,
    openInitiallyDocked: true,
    redockOnReopen: true,
  });
}

/** Opent het uitgelichte vervolgpaneel "Mens na 2027". */
export function openHdLocksKeysPostShiftPanel(
  floatingPanels: FloatingPanelService,
  gateInfo: HdGateInfoService,
  lang: string,
): void {
  openHdLocksKeysReadingPanel(floatingPanels, gateInfo, lang, 'postShift');
}
